import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { api } from "../App";
import { styledToast as toast } from "@/components/ui/sonner";
import { format } from "date-fns";
import { Send, MessageSquare } from "lucide-react";
import { useTranslation } from 'react-i18next';

export default function MessagePanel({ applicationId, currentUser, farmerId, officialId }) {
  const { t } = useTranslation();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadMessages();
  }, [applicationId]);

  const loadMessages = async () => {
    try {
      const response = await api.get(`/applications/${applicationId}/messages`);
      setMessages(response.data);
    } catch (error) {
      console.error("Failed to load messages");
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    setLoading(true);
    try {
      const receiverId = currentUser.role === "farmer" ? officialId : farmerId;
      
      if (!receiverId) {
        toast.error("Cannot send message - recipient not available");
        setLoading(false);
        return;
      }

      await api.post(`/applications/${applicationId}/messages`, {
        text: newMessage,
        receiver_id: receiverId,
      });
      setNewMessage("");
      toast.success("Message sent");
      loadMessages();
    } catch (error) {
      toast.error("Failed to send message");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="p-6 bg-white border border-stone-200 shadow-sm">
      <h2 className="text-xl font-manrope font-bold text-slate-900 mb-6 flex items-center gap-2">
        <MessageSquare className="w-5 h-5 text-emerald-700" />
        {t('messages.title')}
      </h2>

      <ScrollArea className="h-96 mb-4 p-3 border border-stone-200 rounded-lg bg-stone-50">
        {messages.length === 0 ? (
          <div className="text-center py-8">
            <MessageSquare className="w-10 h-10 mx-auto mb-2 text-slate-300" />
            <p className="text-sm text-slate-500">{t('messages.noMessages')}</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="messages-list">
            {messages.map((message) => {
              const isCurrentUser = message.sender_id === currentUser.id;
              return (
                <div
                  key={message.id}
                  className={`flex ${isCurrentUser ? "justify-end" : "justify-start"}`}
                  data-testid={`message-${message.id}`}
                >
                  <div
                    className={`max-w-[75%] rounded-lg p-3 ${
                      isCurrentUser
                        ? "bg-emerald-800 text-white"
                        : "bg-white border border-stone-200 text-slate-900"
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <p className={`text-xs font-medium ${
                        isCurrentUser ? "text-emerald-100" : "text-slate-600"
                      }`}>
                        {message.sender_name}
                      </p>
                      <span className={`text-xs capitalize px-1.5 py-0.5 rounded text-[10px] ${
                        isCurrentUser 
                          ? "bg-emerald-700 text-emerald-100"
                          : "bg-stone-100 text-slate-600"
                      }`}>
                        {message.sender_role}
                      </span>
                    </div>
                    <p className="text-sm mb-1">{message.text}</p>
                    <p className={`text-[10px] ${
                      isCurrentUser ? "text-emerald-200" : "text-slate-400"
                    }`}>
                      {format(new Date(message.created_at), "MMM dd, h:mm a")}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </ScrollArea>

      <form onSubmit={handleSendMessage} className="space-y-3" data-testid="message-form">
        <Textarea
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder={t('messages.placeholder')}
          rows={3}
          disabled={loading}
          data-testid="message-input"
          className="resize-none"
        />
        <Button
          type="submit"
          disabled={loading || !newMessage.trim()}
          className="bg-emerald-800 hover:bg-emerald-900 text-white w-full"
          data-testid="send-message-button"
        >
          <Send className="w-4 h-4 mr-2" />
          {loading ? t('messages.sending') : t('messages.sendMessage')}
        </Button>
      </form>
    </Card>
  );
}